import {
  useState,
  useMemo,
  useEffect,
  useCallback,
  useRef,
  useContext,
  Fragment,
} from "react";
import {
  Text,
  ActionButtonGroup,
  ActionButton,
  ProgressCircle,
  ButtonGroup,
  Button,
} from "@react-spectrum/s2";
import { TextField as AriaTextField, Input } from "react-aria-components";
import MagicWandIcon from "@react-spectrum/s2/icons/MagicWand";
import AudioWaveIcon from "@react-spectrum/s2/icons/AudioWave";
import SpeedFastIcon from "@react-spectrum/s2/icons/SpeedFast";
import AlertNoticeIcon from "@react-spectrum/s2/illustrations/linear/AlertNotice";
import { TextToSpeech } from "@app/components/TextToSpeech";
import { getTrimmedDuration } from "@app/components/AudioPlayer";
import { InlineNotice } from "@app/components/InlineNotice";

import { useVideo, useVideoTemplate } from "@app/hooks/videos";
import { usePresignURL } from "@app/hooks/s3";
import { VideoWizardContext } from "@app/views/projects/videoWizardContext";
import { Step } from "./Step";
import { useUserProfile } from "@app/hooks/useIMS";
import { trackVoiceoverEvent, AnalyticsEventNames } from "@app/utils/analytics";
import {
  useVoices,
  useVoices3p,
  useGenerateSpeech,
  useGenerateSpeech3p,
  useFixPronunciation,
  getPronunciations,
  TTS_MODEL,
  DEFAULT_TTS_MODEL,
} from "@app/hooks/textToSpeech";

import {
  style,
  iconStyle,
} from "@react-spectrum/s2/style" with { type: "macro" };

const inputWrapperStyle = style({
  display: "inline-flex",
  "--borderColor": {
    type: "color",
    value: "gray-500",
  },
});

const inputStyle = style({
  width: "max",
  minWidth: 0,
  font: "body-xl",
  fontWeight: "medium",
  backgroundColor: "transparent",
  borderWidth: 0,
  // paddingX: 2,
  outline: "none",
  textDecoration: {
    isFocused: "none",
    default: "underline",
  },
  outlineColor: {
    isFocused: "gray-900",
    default: "transparent",
  },
  outlineWidth: 2,
  outlineStyle: "solid",
  outlineOffset: 2,
  borderRadius: "sm",

  borderStyle: "none",
  resize: "none",
  overflowX: "hidden",
});

const inputInlineStyle = {
  fieldSizing: "content",
  textUnderlineOffset: 4,
  textDecorationThickness: 1.5,
  textDecorationColor: "var(--borderColor)",
  textDecorationStyle: "dashed",
};

export const getMinDuration = (duration) => duration - 1.5;
export const getMaxDuration = (duration) => duration + 0.1;

const PLACEHOLDER_REGEX = /\{\{([\w\d\-]+)\}\}/g;

export const getTemplatePlaceholderKeys = (text) =>
  Array.from(
    new Set(
      text
        ?.match(/\{\{([\w\d\-]+)\}\}/g)
        ?.map((match) => match.replace(/\{\{|\}\}/g, "")) ?? [],
    ),
  );

export const resolveVoiceoverTemplateText = (
  text,
  { placeholders = {}, parts = {}, campaign = {} } = {},
) =>
  text?.replace(
    PLACEHOLDER_REGEX,
    (match, key) =>
      placeholders?.[key] ?? parts?.[key] ?? campaign?.[key] ?? "",
  ) ?? "";

export const validateAudioDuration = (duration, maxDuration, minDuration) => {
  const _maxDuration = maxDuration ?? getMaxDuration(duration);
  const _minDuration = minDuration ?? getMinDuration(duration);

  if (duration > _maxDuration) {
    return {
      isInvalid: true,
      errorMessage: [
        `The generated voiceover is too long (${duration.toFixed(2)}s, max is ${_maxDuration}s)`,
        `Shorten the text and regenerate`,
      ],
    };
  } else if (duration < _minDuration) {
    return {
      isInvalid: true,
      errorMessage: [
        `The generated voiceover is too short (${duration.toFixed(2)}s, min is ${_minDuration}s)`,
        `Lengthen the text and regenerate`,
      ],
    };
  } else {
    return { isInvalid: false, errorMessage: null };
  }
};

export const VoiceoverAsset = ({ videoId, assetName, isStandalone }) => {
  const user = useUserProfile();
  const video = useVideo(videoId);
  const template = useVideoTemplate(video.data?.templateId);
  const { uploadAssets, updateAssets, editCampaign } =
    useContext(VideoWizardContext);

  const asset = useMemo(
    () => video.data?.assets.find((a) => a.name === assetName),
    [video.data?.assets, assetName],
  );
  const s3Asset = usePresignURL(asset?.filePath);

  const templateAsset = useMemo(
    () => template.data?.assets.find((a) => a.name === assetName),
    [template.data?.assets, assetName],
  );

  const defaultVoiceoverFilePath = useMemo(() => {
    const rootPath = video.data?.mogrtRootPath ?? template.data?.rootPath;
    if (!rootPath) {
      return null;
    }
    return `${rootPath.replace(/\/$/, "")}/vo/${assetName}.wav`;
  }, [video.data?.mogrtRootPath, template.data?.rootPath, assetName]);

  const selectedCampaign = useMemo(
    () =>
      video.data?.campaigns?.find(
        (item) => item.id === video.data?.selectedCampaignIds?.[0],
      ),
    [video.data?.campaigns, video.data?.selectedCampaignIds],
  );

  const placeholderKeys = useMemo(
    () => getTemplatePlaceholderKeys(templateAsset?.text),
    [templateAsset?.text],
  );

  const textFragments = useMemo(
    () => (templateAsset?.text ?? "").split(PLACEHOLDER_REGEX),
    [templateAsset?.text],
  );

  const [placeholders, setPlaceholders] = useState({});

  const textParts = useMemo(
    () =>
      placeholderKeys.reduce((acc, key) => {
        acc[key] =
          placeholders?.[key] ??
          asset?.parts?.[key] ??
          selectedCampaign?.[key] ??
          "";
        return acc;
      }, {}),
    [placeholderKeys, placeholders, asset?.parts, selectedCampaign],
  );

  const hasTextParts = placeholderKeys.length > 0;

  const originalText = useMemo(() => {
    return resolveVoiceoverTemplateText(
      asset?.text ?? templateAsset?.text ?? "",
      {
        placeholders,
        parts: asset?.parts,
        campaign: selectedCampaign,
      },
    );
  }, [
    asset?.text,
    templateAsset?.text,
    placeholders,
    asset?.parts,
    selectedCampaign,
  ]);

  const [_model, setModel] = useState();
  const model =
    _model ??
    asset?.voiceConfig?.model ??
    templateAsset?.voiceConfig?.model ??
    DEFAULT_TTS_MODEL;

  const [_selectedVoiceId, setSelectedVoiceId] = useState();
  const selectedVoiceId =
    _selectedVoiceId ??
    asset?.voiceId ??
    asset?.voiceConfig?.voiceId ??
    templateAsset?.voiceConfig?.voiceId ??
    "v123";

  const [_text, setText] = useState();
  const text = _text ?? originalText;

  const [_speed, setSpeed] = useState();
  const speed =
    _speed ??
    asset?.voiceConfig?.speed ??
    templateAsset?.voiceConfig?.speed ??
    1;

  const [_pitch, setPitch] = useState();
  const pitch =
    _pitch ??
    asset?.voiceConfig?.pitch ??
    templateAsset?.voiceConfig?.pitch ??
    0;

  const [duration, setDuration] = useState(0);

  const [_isInvalid, setInvalid] = useState(null);
  const isInvalid = _isInvalid ?? asset?.isInvalid ?? false;

  const [errorMessage, setErrorMessage] = useState(null);

  const isElevenLabs = model === TTS_MODEL.ELEVENLABS;
  const fireflyVoices = useVoices();
  const elevenLabsVoices = useVoices3p({ enabled: isElevenLabs });
  const voices = isElevenLabs ? elevenLabsVoices : fireflyVoices;
  const pendingScriptRef = useRef(null);

  const handleModelChange = useCallback((nextModel) => {
    setModel(nextModel);
    setSelectedVoiceId(null);
  }, []);

  const captureScriptSnapshot = useCallback(
    (overrides = {}) => {
      const snapshot = {
        text: hasTextParts
          ? (templateAsset?.text ?? overrides.text)
          : (overrides.text ?? text ?? templateAsset?.text),
        voiceId: overrides.voiceId ?? selectedVoiceId,
        voiceConfig: overrides.voiceConfig ?? { speed, pitch, model },
        parts: { ...(overrides.parts ?? textParts) },
      };
      pendingScriptRef.current = snapshot;
      return snapshot;
    },
    [
      hasTextParts,
      templateAsset?.text,
      text,
      selectedVoiceId,
      speed,
      pitch,
      model,
      textParts,
    ],
  );

  const buildVoiceoverAssetPayload = useCallback(
    (audioSrc, variables = {}) => {
      const snapshot = pendingScriptRef.current ?? {};
      return {
        name: assetName,
        type: "audio",
        src: audioSrc,
        text: hasTextParts
          ? (templateAsset?.text ?? variables.text ?? snapshot.text)
          : (variables.text ?? snapshot.text ?? text ?? templateAsset?.text),
        voiceId: variables.voiceId ?? snapshot.voiceId ?? selectedVoiceId,
        voiceConfig: variables.voiceConfig ??
          snapshot.voiceConfig ?? { speed, pitch, model },
        parts: {
          ...(variables.parts ?? snapshot.parts ?? textParts),
        },
        generatedAt: Date.now(),
        isRegenerated: true,
      };
    },
    [
      assetName,
      hasTextParts,
      templateAsset?.text,
      text,
      selectedVoiceId,
      speed,
      pitch,
      model,
      textParts,
    ],
  );

  const persistScriptMetadata = useCallback(
    async (script) => {
      if (!asset?.filePath) {
        return;
      }

      await updateAssets.mutateAsync({
        assets: [
          {
            name: assetName,
            type: "audio",
            filePath: asset.filePath,
            ...script,
          },
        ],
      });
    },
    [asset?.filePath, assetName, updateAssets],
  );

  const handleUseDefaultVoiceover = useCallback(async () => {
    if (!defaultVoiceoverFilePath) {
      return;
    }

    setInvalid(null);
    setErrorMessage(null);

    const script = captureScriptSnapshot();

    await uploadAssets.mutateAsync({
      assets: [
        {
          name: assetName,
          type: "audio",
          filePath: defaultVoiceoverFilePath,
          text: script.text,
          voiceId: script.voiceId,
          voiceConfig: script.voiceConfig,
          parts: { ...script.parts },
          generatedAt: Date.now(),
          // Template default track — not a regeneration, so it keeps the whole
          // voiceover on the pre-rendered preview.
          isRegenerated: false,
        },
      ],
    });
  }, [
    defaultVoiceoverFilePath,
    captureScriptSnapshot,
    uploadAssets,
    assetName,
  ]);

  const generateSpeechCallbacks = {
    onMutate: () => {
      setInvalid(null);
      setErrorMessage(null);
    },
    onSuccess: async (audioSrc, variables) => {
      return uploadAssets.mutateAsync({
        assets: [buildVoiceoverAssetPayload(audioSrc, variables)],
      });
    },
  };

  const generateSpeechFirefly = useGenerateSpeech(generateSpeechCallbacks);
  const generateSpeech3p = useGenerateSpeech3p(generateSpeechCallbacks);
  const generateSpeech = isElevenLabs
    ? generateSpeech3p
    : generateSpeechFirefly;
  const isGeneratingSpeech =
    generateSpeechFirefly.isPending || generateSpeech3p.isPending;

  const fixPronunciation = useFixPronunciation({
    onSuccess: (data) => {
      setText(data.text);
      setPlaceholders(data.parts);
    },
  });

  const handleFixPronounciation = useCallback(() => {
    fixPronunciation.mutate({
      text: originalText,
      parts: textParts,
    });
    trackVoiceoverEvent(AnalyticsEventNames.VOICEOVER_PRONUNCIATION_FIXED, {
      user: { email: user?.email },
      videoId,
      templateId: video.data?.templateId,
    });
  }, [fixPronunciation]);

  const selectedVoice = useMemo(() => {
    if (isElevenLabs) {
      return (
        voices.data?.find((voice) => voice.voiceId === selectedVoiceId) ?? {}
      );
    }
    const voiceId = selectedVoiceId?.split("_")[0] + "_1";
    return voices.data?.find((voice) => voice.voiceId === voiceId) ?? {};
  }, [selectedVoiceId, voices.data, isElevenLabs]);

  const parts = useMemo(() => {
    if (!asset?.parts) return {};

    return Object.entries(asset.parts).reduce((acc, [key, value]) => {
      const pronunciations = getPronunciations(value);
      const text = pronunciations.reduce(
        (acc, { match, key }) => acc.replaceAll(match, key),
        value,
      );

      acc[key] = text;
      return acc;
    }, {});
  }, [asset?.parts]);

  const hasChanged = useMemo(
    () =>
      !!asset?.parts &&
      Object.entries(parts).some(([key, value]) => {
        return value !== selectedCampaign?.[key];
      }),
    [parts, selectedCampaign],
  );

  const handlePlayerReady = useCallback(
    (ws) => {
      const _duration = getTrimmedDuration(ws);
      setDuration(_duration);

      const validation = validateAudioDuration(
        _duration,
        templateAsset?.maxDuration,
        templateAsset?.minDuration,
      );
      setInvalid(validation.isInvalid);
      setErrorMessage(validation.errorMessage);
    },
    [templateAsset?.maxDuration, templateAsset?.minDuration],
  );

  const handleGenerate = useCallback(
    async (variables = { voiceConfig: {} }) => {
      const { voiceId: templateVoiceId, ...templateVoiceConfig } =
        templateAsset?.voiceConfig || {};

      const voiceId = isElevenLabs
        ? (variables.voiceId ??
          selectedVoice.voiceId ??
          templateVoiceId ??
          voices.data?.[0]?.voiceId)
        : (variables.voiceId ??
          selectedVoiceId ??
          selectedVoice.voiceId ??
          templateVoiceId ??
          "v123");

      const voiceConfig = {
        ...templateVoiceConfig,
        speed,
        pitch,
        model,
        ...variables.voiceConfig,
      };

      const script = captureScriptSnapshot({
        voiceId,
        voiceConfig,
        parts: variables.parts,
        text: variables.text,
      });

      await persistScriptMetadata(script);

      generateSpeech.mutate({
        text: hasTextParts
          ? (variables.text ?? originalText)
          : (variables.text ?? text),
        voiceId,
        parts: { ...script.parts },
        voiceConfig,
      });
    },
    [
      hasTextParts,
      originalText,
      text,
      captureScriptSnapshot,
      persistScriptMetadata,
      templateAsset?.voiceConfig,
      selectedVoice?.voiceId,
      selectedVoiceId,
      pitch,
      speed,
      model,
      isElevenLabs,
      voices.data,
      generateSpeech.mutate,
    ],
  );

  const handleRegenerateWithSpeed = useCallback(() => {
    const newSpeed =
      speed + Math.ceil(duration - templateAsset.maxDuration) * 0.05;
    setSpeed(newSpeed);

    handleGenerate({ voiceConfig: { speed: newSpeed } });
  }, [speed, handleGenerate, duration, templateAsset?.maxDuration]);

  // useEffect(() => {
  //   if (video.isSuccess && template.isSuccess) {
  //     setSpeed(
  //       asset?.voiceConfig?.speed ?? templateAsset?.voiceConfig?.speed ?? 1,
  //     );
  //     setPitch(
  //       asset?.voiceConfig?.pitch ?? templateAsset?.voiceConfig?.pitch ?? 0,
  //     );

  //     if (asset?.voiceConfig?.voiceId ?? templateAsset?.voiceConfig?.voiceId) {
  //       setSelectedVoiceId(
  //         asset?.voiceConfig?.voiceId ?? templateAsset?.voiceConfig?.voiceId,
  //       );
  //     }

  //     if (
  //       typeof asset?.isInvalid !== "undefined" &&
  //       asset?.isInvalid !== isInvalid
  //     ) {
  //       setInvalid(asset.isInvalid);
  //     }
  //   }
  // }, [
  //   video.isSuccess,
  //   template.isSuccess,
  //   asset,
  //   templateAsset,
  //   pitch,
  //   speed,
  //   isInvalid,
  // ]);

  useEffect(() => {
    if (
      hasChanged &&
      !editCampaign.isPending &&
      !uploadAssets.isPending &&
      !updateAssets.isPending &&
      !isGeneratingSpeech
    ) {
      editCampaign.mutate({
        id: selectedCampaign?.id,
        ...parts,
      });
    }
  }, [
    hasChanged,
    editCampaign.isPending,
    editCampaign.mutate,
    selectedCampaign?.id,
    parts,
    uploadAssets.isPending,
    updateAssets.isPending,
    isGeneratingSpeech,
  ]);

  const hasCampaignValues =
    !hasTextParts || placeholderKeys.every((key) => Boolean(textParts[key]));

  const shouldGenerate =
    !asset &&
    !voices.isLoading &&
    !editCampaign.isPending &&
    !fixPronunciation.isPending &&
    !isGeneratingSpeech &&
    !uploadAssets.isPending &&
    hasCampaignValues;

  const isDefaultVoiceover =
    !hasTextParts &&
    !!defaultVoiceoverFilePath &&
    text === (templateAsset?.text ?? "");

  useEffect(() => {
    if (!shouldGenerate) {
      return;
    }

    if (isDefaultVoiceover) {
      handleUseDefaultVoiceover();
      return;
    }

    fixPronunciation.mutate(
      {
        text: originalText,
        parts: textParts,
      },
      {
        onSuccess: (data) => {
          handleGenerate({
            text: data.text,
            parts: data.parts,
          });
        },
      },
    );
  }, [
    shouldGenerate,
    isDefaultVoiceover,
    handleUseDefaultVoiceover,
    handleGenerate,
    originalText,
    textParts,
    fixPronunciation.mutate,
  ]);

  // useEffect(() => {
  //   if (
  //     !!asset &&
  //     asset.isInvalid !== isInvalid &&
  //     !generateSpeech.isPending &&
  //     !updateAssets.isPending
  //   ) {
  //     updateAssets.mutate({
  //       assets: [{ ...asset, isInvalid, errorMessage }],
  //     });
  //   }
  // }, [
  //   asset,
  //   isInvalid,
  //   errorMessage,
  //   generateSpeech.isPending,
  //   updateAssets.isPending,
  // ]);

  if (!templateAsset || !asset) {
    return (
      <Step.Loading
        title="Generating voiceover"
        subtitle="This may take a few moments, so please keep this tab open."
        ariaLabel="Generating voiceover"
        icon={<AudioWaveIcon />}
      />
    );
  }

  return (
    <div
      className={style({
        display: "flex",
        flexDirection: "column",
        gap: 32,
      })}
    >
      <Step.Header
        // title={templateAsset?.label}
        subtitle={templateAsset?.description}
      />

      <div
        className={style({ display: "flex", flexDirection: "column", gap: 32 })}
      >
        <div
          className={
            hasTextParts
              ? style({
                  display: "flex",
                  flexDirection: "column",
                  gap: 24,
                  padding: 16,
                  paddingTop: 12,
                  backgroundColor: "base",
                  borderWidth: 2,
                  borderStyle: "solid",
                  borderColor: {
                    isInvalid: "notice-700",
                    default: "gray-100",
                  },
                  borderRadius: "lg",
                  transition: "colors",
                })({ isInvalid })
              : ""
          }
        >
          {!!hasTextParts && (
            <div className={style({ display: "inline", gap: 8 })}>
              {textFragments.map((key, index) => (
                <Fragment key={index}>
                  {index % 2 === 1 ? (
                    <AriaTextField
                      aria-label={key}
                      className={inputWrapperStyle}
                    >
                      <Input
                        value={
                          placeholders?.[key] ??
                          textParts?.[key] ??
                          selectedCampaign?.[key] ??
                          ""
                        }
                        onChange={(e) =>
                          setPlaceholders((prev) => ({
                            ...prev,
                            [key]: e.target.value,
                          }))
                        }
                        className={inputStyle}
                        style={inputInlineStyle}
                      />
                    </AriaTextField>
                  ) : (
                    <Text styles={style({ font: "body-xl", color: "detail" })}>
                      {key}
                    </Text>
                  )}
                </Fragment>
              ))}
            </div>
          )}

          <TextToSpeech
            audioUrl={s3Asset.data?.presignURL}
            voiceId={selectedVoice.voiceId}
            voices={voices.data}
            model={model}
            onModelChange={handleModelChange}
            segments={templateAsset?.segments ?? []}
            isInvalid={isInvalid}
            text={hasTextParts ? undefined : text}
            onTextChange={hasTextParts ? undefined : setText}
            defaultSpeed={speed}
            onSpeedChange={setSpeed}
            defaultPitch={pitch}
            onPitchChange={setPitch}
            onGenerate={({ text, voiceId, voiceConfig }) => {
              if (s3Asset.data?.presignURL) {
                trackVoiceoverEvent(
                  AnalyticsEventNames.VOICEOVER_FRAGMENT_REGENERATED,
                  {
                    user: { email: user?.email },
                    videoId,
                    templateId: video.data?.templateId,
                  },
                );
              }
              handleGenerate({ text, voiceId, voiceConfig });
            }}
            onPreviewPlay={() =>
              trackVoiceoverEvent(
                AnalyticsEventNames.FRAGMENTED_AUDIO_PREVIEWED,
                {
                  user: { email: user?.email },
                  videoId,
                  templateId: video.data?.templateId,
                },
              )
            }
            onReady={handlePlayerReady}
            isPending={isGeneratingSpeech}
            isDisabled={fixPronunciation.isPending}
            onVoiceSelect={setSelectedVoiceId}
            maxCharacters={650}
            size="XL"
          >
            <ActionButtonGroup isQuiet>
              <ActionButton
                isDisabled={isGeneratingSpeech || fixPronunciation.isPending}
                onPress={handleFixPronounciation}
              >
                {fixPronunciation.isPending ? (
                  <ProgressCircle
                    aria-label="Generating pronunciations"
                    size="S"
                    staticColor="auto"
                    slot="icon"
                    isIndeterminate
                  />
                ) : (
                  <MagicWandIcon />
                )}
                <Text>Fix pronunciation</Text>
              </ActionButton>
            </ActionButtonGroup>
          </TextToSpeech>
        </div>

        {isInvalid && (
          <InlineNotice errorMessage={errorMessage}>
            <ButtonGroup styles={style({ marginStart: 64 })}>
              <Text styles={style({ font: "ui" })}>or</Text>
              <Button variant="secondary" onPress={handleRegenerateWithSpeed}>
                <SpeedFastIcon />
                <Text>Generate</Text>
              </Button>
              <Text styles={style({ font: "ui" })}>
                with adjusted voice speed
              </Text>
            </ButtonGroup>
          </InlineNotice>
        )}
      </div>
    </div>
  );
};
